/**
 * API keys feature — queries + mutations.
 *
 * Platform-scoped: every endpoint is `/api/admin/v1/api-keys`, no
 * tenant header. Only platform admins can mutate; the server
 * enforces the permission.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  apiKeysListResponseSchema,
  createApiKeyResponseSchema,
  type ApiKeysListResponse,
  type CreateApiKeyInput,
  type CreateApiKeyResponse,
} from "./schemas";

const BASE = "/api/admin/v1/api-keys";

export async function fetchApiKeys(): Promise<ApiKeysListResponse> {
  const raw = await apiFetch<unknown>(BASE);
  return apiKeysListResponseSchema.parse(raw);
}

export function useApiKeysQuery() {
  return useQuery<ApiKeysListResponse, ApiError>({
    queryKey: queryKeys.apiKeys.list,
    queryFn: fetchApiKeys,
  });
}

// toWireBody maps the dialog's days-based input to the
// backend's CreateAPIKeyRequest (seconds-based TTL). target_role
// is omitted from the body when empty so the backend falls
// through to its "inherit the caller's role" branch.
function toWireBody(input: CreateApiKeyInput): Record<string, unknown> {
  const body: Record<string, unknown> = { name: input.name };
  if (input.target_role) body.target_role = input.target_role;
  if (input.expires_in_days && input.expires_in_days > 0) {
    body.ttl_seconds = input.expires_in_days * 86400;
  }
  return body;
}

export function useCreateApiKeyMutation() {
  const qc = useQueryClient();
  return useMutation<CreateApiKeyResponse, ApiError, CreateApiKeyInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(BASE, {
          method: "POST",
          body: toWireBody(input),
        }).then((raw) => createApiKeyResponseSchema.parse(raw)),
      ),
    onSuccess: () => {
      // Invalidate the list so the new key's metadata row
      // shows up. The raw_token deliberately does NOT land in
      // any cache — the caller reads it inline from the
      // mutation's resolved promise and discards.
      qc.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
    },
  });
}

// Revoke uses POST {id}/revoke — NOT DELETE {id}. The backend
// apikey handler mounts Revoke as a POST verb (see
// internal/admin/apikey/handler.go: `r.Post("/api-keys/{id}/revoke", h.Revoke)`)
// so the audit trail reads "revoke" as an intentional action
// rather than a generic resource deletion. Sending DELETE
// produces a 405 "method not allowed" from chi's router.
export function useRevokeApiKeyMutation() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (keyId) =>
      withStepUp(() =>
        apiFetch<void>(`${BASE}/${encodeURIComponent(keyId)}/revoke`, {
          method: "POST",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
    },
  });
}
